import { useMemo, useState } from "react";
import { type HttpError, useTable, useTranslate, useUpdate, useCreate } from "@refinedev/core";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import { RefineListView } from "@/components";
import { FormProvider, useForm } from "react-hook-form";
import { IRolePermission } from "@/utils/zod/rolePermissionSchema";
import { IconButton, Tooltip } from "@mui/material";
import { Stack } from "@mui/material";
import { CreateButton, DeleteButton } from "@refinedev/mui";
import { Close, EditOutlined, Save } from "@mui/icons-material";
import { getCellValue } from "@/components/role-permissions/getCellValue";
import { TextFieldElement } from "react-hook-form-mui";
import { useTableState } from "@/hooks/useTableState";

export const RolePermissionList = () => {
  const t = useTranslate();
  const { tableState, onColumnFiltersChange, onGlobalFilterChange, onSortingChange } = useTableState<IRolePermission>();

  const { tableQuery } = useTable<IRolePermission[], HttpError, IRolePermission>({
    resource: "role_permissions",
  });

  const { mutate: updateMutation } = useUpdate();
  const { mutate: createMutation } = useCreate();

  const roles = tableQuery?.data?.data.filter((role) => role.name !== "super_admin" && role.name !== "ai_bot") ?? [];

  // State to track which row is being edited
  const [editingRow, setEditingRow] = useState<string | null>(null);

  // State to track if we're adding a new role
  const [isAddingNewRole, setIsAddingNewRole] = useState(false);

  // Combined data including the new role if being added
  const combinedData = useMemo(() => {
    if (!isAddingNewRole) return roles;

    // Create a new role with default restricted permissions
    const newRole: IRolePermission = {
      name: "new_role",
      admin_panel: {
        create: "restricted",
        edit: "restricted",
        delete: "restricted",
        view: "restricted",
      },
      super_admin_panel: {
        create: "restricted",
        edit: "restricted",
        delete: "restricted",
        view: "restricted",
      },
    };

    return [...roles, newRole];
  }, [roles, isAddingNewRole]);

  // Create initial form values from roles data
  const initialFormValues = useMemo(() => {
    const formValues = combinedData.reduce((acc: Record<string, any>, role) => {
      acc[role.name] = {
        admin_panel: { ...role.admin_panel },
        super_admin_panel: { ...role.super_admin_panel }
      };
      return acc;
    }, {});

    return formValues;
  }, [combinedData, isAddingNewRole]);

  const methods = useForm<any>({
    values: initialFormValues,
  });

  // Handle toggle all permissions at once
  const handleToggleAll = (rowName: string) => {
    if (editingRow !== rowName && rowName !== "new_role") return;

    // Get all current values
    const formPrefix = `${rowName}.admin_panel`;
    const viewValue = methods.getValues(`${formPrefix}.view`) || "restricted";
    
    // Determine new value
    let newValue = "allowed";
    
    // If all permissions are the same and allowed, toggle to restricted
    // Otherwise, set all to allowed
    if (viewValue === "allowed") {
      newValue = "restricted";
    }

    // Update all values
    methods.setValue(`${formPrefix}.view`, newValue, { shouldDirty: true });
    methods.setValue(`${formPrefix}.create`, newValue, { shouldDirty: true });
    methods.setValue(`${formPrefix}.edit`, newValue, { shouldDirty: true });
    methods.setValue(`${formPrefix}.delete`, newValue, { shouldDirty: true });
  };

  // Handle save changes for existing role
  const handleSave = (rowName: string) => {
    // If it's a new role, handle differently
    if (rowName === "new_role") {
      handleSaveNewRole();
      return;
    }

    const rowData = methods.getValues(rowName);
    // Here you would typically call your update API
    updateMutation({
      resource: "role_permissions",
      id: rowName,
      values: {
        ...rowData,
        name: rowName,
      },
    });

    setEditingRow(null);
  };

  // Handle save for new role
  const handleSaveNewRole = () => {
    const newRoleData = methods.getValues("new_role");
    const newName = newRoleData.name?.trim();

    if (!newName) return;

    // Here you would typically call your create API
    // For now, we'll just reset the state
    createMutation({
      resource: "role_permissions",
      values: newRoleData,
    });

    // Reset states
    setIsAddingNewRole(false);
    setEditingRow(null);
  };

  // Handle cancel edit
  const handleCancel = () => {
    if (isAddingNewRole) {
      setIsAddingNewRole(false);
    }
    methods.reset(initialFormValues);
    setEditingRow(null);
  };

  const handleEditClick = (rowName: string) => {
    methods.reset(initialFormValues);
    setEditingRow(rowName);
  };

  const handleAddNewRole = () => {
    setIsAddingNewRole(true);
    setEditingRow("new_role");
  };

  const columns = useMemo<MRT_ColumnDef<IRolePermission>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        Cell: ({ row }) => {
          const isNewRole = row.original.name === "new_role";

          if (isNewRole) {
            return (
              <TextFieldElement
                name="new_role.name"
                fullWidth
                size="small"
                placeholder="Enter role name"
                autoFocus
              />
            );
          }

          return row.original.name;
        },
      },
      {
        accessorKey: "admin_panel.all",
        header: "Admin Panel Access",
        Cell: ({ row }) => {
          const isEditing = editingRow === row.original.name;
          const isNewRole = row.original.name === "new_role";
          const formPrefix = row.original.name === "new_role" ? "new_role.admin_panel" : `${row.original.name}.admin_panel`;
          
          // Use watch instead of getValues to trigger re-renders when values change
          const view = methods.watch(`${formPrefix}.view`) || "restricted";
          
          return getCellValue(
            isEditing || isNewRole, 
            view, 
            () => handleToggleAll(row.original.name)
          );
        },
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const isEditing = editingRow === row.original.name;
          const isNewRole = row.original.name === "new_role";
          const newRoleName = isNewRole ? methods.getValues("new_role.name")?.trim() : null;
          const isSaveDisabled = isNewRole && !newRoleName;

          return (
            <Stack direction="row" spacing={1}>
              {isEditing || isNewRole ? (
                <>
                  <Tooltip title={isSaveDisabled ? "Enter a role name" : "Save"}>
                    <span> {/* Wrapper to handle disabled tooltip */}
                      <IconButton
                        color="primary"
                        onClick={() => handleSave(row.original.name)}
                        size="small"
                        disabled={isSaveDisabled}
                      >
                        <Save sx={{ fontSize: 20 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <IconButton
                      color="error"
                      onClick={handleCancel}
                      size="small"
                    >
                      <Close sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Tooltip title="Edit">
                    <IconButton
                      color="primary"
                      onClick={() => handleEditClick(row.original.name)}
                      size="small"
                    >
                      <EditOutlined sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  <DeleteButton
                    hideText
                    resource="role_permissions"
                    recordItemId={row.original.name}
                  />
                </>
              )}
            </Stack>
          );
        },
      },
    ],
    [t, methods.control, editingRow, combinedData, isAddingNewRole]
  );

  return (
    <RefineListView headerButtons={
      (props) => [
        <CreateButton
          {...props.createButtonProps}
          key="create"
          size="medium"
          sx={{ height: "40px" }}
          onClick={handleAddNewRole}
        >
          {t("role-permissions.titles.create")}
        </CreateButton>
      ]}>
      <FormProvider {...methods}>
        <MaterialReactTable
          columns={columns}
          data={combinedData}
          enableTopToolbar
          enableBottomToolbar
          enableColumnFilters
          enableGlobalFilter
          enablePagination
          muiTableBodyRowProps={{ hover: true }}
          muiPaginationProps={{
            rowsPerPageOptions: [10, 20, 40, 80],
            showFirstButton: true,
            showLastButton: true
          }}
          state={{
            ...tableState,
            isLoading: tableQuery?.isLoading,
          }}
          onColumnFiltersChange={onColumnFiltersChange}
          onGlobalFilterChange={onGlobalFilterChange}
          onSortingChange={onSortingChange}
          muiToolbarAlertBannerProps={
            tableQuery?.isError
              ? {
                color: 'error',
                children: 'Error loading data',
              }
              : undefined
          }
          enableColumnResizing
          enableFullScreenToggle
          enableDensityToggle
          enableGlobalFilterModes
          positionGlobalFilter="right"
          getRowId={(row) => row.name}
        />
      </FormProvider>
    </RefineListView>
  );
};
